import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Cart View Component
 *
 * Displays cart contents as a horizontal carousel within the chat widget.
 * Shows cart items as product cards matching ProductCard design, with
 * manual removal (X button) and variation info.
 *
 * @package WooAIChatbot
 */
import { useRef, useState, useCallback, useEffect } from 'react';
import { ShoppingCart, CreditCard, Package, X } from 'lucide-react';
import { wac } from '../utils/wacClassNames';
import { useTranslation } from '../../hooks/useTranslation';
/**
 * CartView Component
 *
 * Renders cart contents as a carousel of product cards
 */
export function CartView({ cart, onCheckout, onViewCart, onRemoveItem }) {
    const { t, direction } = useTranslation();
    const { items, total, checkout_url, cart_url } = cart;
    const scrollRef = useRef(null);
    const [canScrollLeft, setCanScrollLeft] = useState(false);
    const [canScrollRight, setCanScrollRight] = useState(false);
    const [removingKey, setRemovingKey] = useState(null);
    const updateScrollButtons = useCallback(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        const maxScroll = el.scrollWidth - el.clientWidth;
        const absScroll = Math.abs(el.scrollLeft);
        setCanScrollLeft(absScroll > 4);
        setCanScrollRight(absScroll < maxScroll - 4);
    }, []);
    useEffect(() => {
        const el = scrollRef.current;
        if (!el)
            return;
        updateScrollButtons();
        el.addEventListener('scroll', updateScrollButtons, { passive: true });
        const observer = new ResizeObserver(updateScrollButtons);
        observer.observe(el);
        return () => {
            el.removeEventListener('scroll', updateScrollButtons);
            observer.disconnect();
        };
    }, [updateScrollButtons, items]);
    const scroll = useCallback((dir) => {
        const el = scrollRef.current;
        if (!el)
            return;
        const cardWidth = 262; // 250px card + 12px gap
        el.scrollBy({ left: dir === 'right' ? cardWidth * 2 : -cardWidth * 2, behavior: 'smooth' });
        setTimeout(() => updateScrollButtons(), 400);
    }, [updateScrollButtons]);
    /**
     * Remove a cart item via WC Store API, then notify parent
     */
    const handleRemoveItem = useCallback(async (itemKey) => {
        if (!itemKey || removingKey)
            return;
        setRemovingKey(itemKey);
        try {
            const storeNonce = window.wooAIChatConfig?.storeApiNonce || '';
            const resp = await fetch('/wp-json/wc/store/v1/cart/remove-item', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...(storeNonce ? { 'Nonce': storeNonce } : {}),
                },
                body: JSON.stringify({ key: itemKey }),
                credentials: 'same-origin',
            });
            if (resp.ok) {
                // Trigger WC frontend events for mini-cart sync
                const jq = window.jQuery;
                if (jq) {
                    jq(document.body).trigger('removed_from_cart');
                    jq(document.body).trigger('wc_fragment_refresh');
                }
                onRemoveItem?.(itemKey);
            }
        }
        catch (err) {
            console.error('[WooAI] Failed to remove cart item:', err);
        }
        finally {
            setRemovingKey(null);
        }
    }, [removingKey, onRemoveItem]);
    // Empty cart state
    if (!items || items.length === 0) {
        return (_jsxs("div", { dir: direction, className: wac('flex flex-col items-center gap-3 p-4 rounded-xl'), style: { background: 'var(--waa-gray-50)' }, children: [_jsx("div", { className: wac('w-12 h-12 rounded-full flex items-center justify-center'), style: { background: 'var(--waa-gray-200)' }, children: _jsx(ShoppingCart, { className: wac('w-6 h-6'), style: { color: 'var(--waa-gray-500)' } }) }), _jsxs("div", { className: wac('text-center'), children: [_jsx("p", { className: wac('text-sm font-medium'), style: { color: 'var(--waa-gray-700)' }, children: t('yourCartEmpty') }), _jsx("p", { className: wac('text-xs mt-1'), style: { color: 'var(--waa-gray-500)' }, children: t('browseProducts') })] })] }));
    }
    const handleCheckout = () => {
        if (onCheckout) {
            onCheckout();
        }
        else if (checkout_url) {
            window.open(checkout_url, '_blank', 'noopener,noreferrer');
        }
    };
    const handleViewCart = () => {
        if (onViewCart) {
            onViewCart();
        }
        else if (cart_url) {
            window.open(cart_url, '_blank', 'noopener,noreferrer');
        }
    };
    /**
     * Safely stringify a variation value that may be a string or an object
     * (Store API returns {raw_attribute, attribute, value} objects).
     */
    const safeVariationValue = (v) => {
        if (v === null || v === undefined)
            return '';
        if (typeof v === 'string')
            return v;
        if (typeof v === 'object') {
            const obj = v;
            return String(obj.value ?? obj.name ?? obj.attribute ?? JSON.stringify(obj));
        }
        return String(v);
    };
    /**
     * Format variation attributes for display.
     * Handles both Record<string, string> and Store API array/object formats.
     */
    const formatVariation = (variation) => {
        if (!variation)
            return null;
        // Store API may return an array of {attribute, value} objects
        if (Array.isArray(variation)) {
            if (variation.length === 0)
                return null;
            return variation.map((v) => {
                const obj = v;
                const rawKey = String(obj.raw_attribute ?? obj.attribute ?? '');
                const label = rawKey
                    .replace(/^attribute_pa_/, '')
                    .replace(/^attribute_/, '')
                    .replace(/_/g, ' ')
                    .replace(/^\w/, (c) => c.toUpperCase()) || 'Variant';
                return { label, value: safeVariationValue(obj.value ?? obj.name) };
            });
        }
        // Plain object Record<string, string | object>
        const entries = Object.entries(variation);
        if (entries.length === 0)
            return null;
        return entries.map(([key, value]) => {
            const label = key
                .replace(/^attribute_pa_/, '')
                .replace(/^attribute_/, '')
                .replace(/_/g, ' ')
                .replace(/^\w/, (c) => c.toUpperCase());
            return { label, value: safeVariationValue(value) };
        });
    };
    return (_jsxs("div", { dir: direction, children: [_jsxs("div", { className: wac('flex items-center gap-2 mb-2'), children: [_jsx(ShoppingCart, { className: wac('w-4 h-4'), style: { color: 'var(--waa-primary)' } }), _jsxs("span", { className: wac('text-sm font-medium'), style: { color: 'var(--waa-gray-900)' }, children: [t('yourCart'), " (", items.length, " ", items.length === 1 ? t('item') : t('items'), ")"] })] }), _jsxs("div", { dir: "ltr", className: "wac-relative wac-w-full wac-min-w-0 wac-max-w-full", children: [canScrollLeft && (_jsx("button", { type: "button", onClick: () => scroll('left'), className: "wac-absolute wac-top-1/2 wac--translate-y-1/2 wac-z-10 wac-w-8 wac-h-8 wac-flex wac-items-center wac-justify-center wac-rounded-full wac-shadow-md wac-transition-opacity wac-opacity-80 hover:wac-opacity-100", style: { left: 0, background: 'var(--waa-bg-primary, #fff)', color: 'var(--waa-gray-700, #374151)' }, "aria-label": "Scroll left", children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("polyline", { points: "15 18 9 12 15 6" }) }) })), canScrollRight && (_jsx("button", { type: "button", onClick: () => scroll('right'), className: "wac-absolute wac-top-1/2 wac--translate-y-1/2 wac-z-10 wac-w-8 wac-h-8 wac-flex wac-items-center wac-justify-center wac-rounded-full wac-shadow-md wac-transition-opacity wac-opacity-80 hover:wac-opacity-100", style: { right: 0, background: 'var(--waa-bg-primary, #fff)', color: 'var(--waa-gray-700, #374151)' }, "aria-label": "Scroll right", children: _jsx("svg", { width: "16", height: "16", viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: "2.5", strokeLinecap: "round", strokeLinejoin: "round", children: _jsx("polyline", { points: "9 18 15 12 9 6" }) }) })), _jsx("div", { ref: scrollRef, className: "wac-w-full wac-min-w-0 wac-overflow-x-auto wac-overflow-y-hidden wac-py-3 wac-px-1", style: {
                            touchAction: 'pan-x',
                            WebkitOverflowScrolling: 'touch',
                            scrollbarWidth: 'thin',
                        }, onTouchMove: (e) => e.stopPropagation(), onWheel: (e) => {
                            if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                                e.stopPropagation();
                            }
                        }, children: _jsx("div", { className: "wac-flex wac-gap-3 wac-flex-nowrap", style: { minWidth: 'max-content' }, children: items.map((item, index) => {
                                const variationAttrs = formatVariation(item.variation);
                                const isRemoving = removingKey === item.key;
                                return (_jsxs("div", { dir: direction, className: "wac-flex wac-flex-col wac-min-w-[250px] wac-max-w-[250px]", style: {
                                        position: 'relative',
                                        backgroundColor: 'var(--waa-card-bg, #FFFFFF)',
                                        color: 'var(--waa-card-text, #111827)',
                                        borderRadius: 0,
                                        border: '1px solid var(--waa-gray-200, #e5e7eb)',
                                        overflow: 'hidden',
                                        opacity: isRemoving ? 0.5 : 1,
                                        transition: 'opacity 0.2s',
                                    }, children: [item.key && (_jsx("button", { type: "button", onClick: () => handleRemoveItem(item.key), disabled: isRemoving, "aria-label": "Remove item", style: {
                                                position: 'absolute',
                                                top: '6px',
                                                right: '6px',
                                                zIndex: 5,
                                                width: '24px',
                                                height: '24px',
                                                borderRadius: '50%',
                                                border: 'none',
                                                background: 'rgba(0, 0, 0, 0.45)',
                                                color: '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                cursor: isRemoving ? 'wait' : 'pointer',
                                                padding: 0,
                                                transition: 'background 0.15s',
                                            }, onMouseEnter: (e) => { e.currentTarget.style.background = 'rgba(0, 0, 0, 0.7)'; }, onMouseLeave: (e) => { e.currentTarget.style.background = 'rgba(0, 0, 0, 0.45)'; }, children: _jsx(X, { style: { width: '14px', height: '14px' } }) })), _jsx("div", { style: { position: 'relative' }, children: item.image ? (_jsx("img", { src: item.image, alt: item.name, className: "wac-w-full", style: { height: '180px', objectFit: 'cover', width: '100%' } })) : (_jsx("div", { className: "wac-w-full wac-flex wac-items-center wac-justify-center", style: { height: '180px', background: 'var(--waa-gray-100)' }, children: _jsx(Package, { className: "wac-w-8 wac-h-8", style: { color: 'var(--waa-gray-400)' } }) })) }), _jsxs("div", { style: { padding: '8px 10px 10px', display: 'flex', flexDirection: 'column', flex: 1 }, children: [_jsx("h3", { style: { fontSize: '12px', fontWeight: 700, lineHeight: 1.3, margin: 0, textAlign: 'center' }, children: item.name.replace(/<[^>]*>/g, ' ').trim() }), _jsx("p", { style: { fontSize: '13px', fontWeight: 700, textAlign: 'center', margin: '4px 0 0', color: 'var(--waa-card-text, #111827)' }, children: item.subtotal }), variationAttrs && variationAttrs.length > 0 && (_jsx("div", { className: "wac-flex wac-flex-wrap wac-gap-1 wac-justify-center", style: { marginTop: '6px' }, children: variationAttrs.map(({ label, value }) => (_jsxs("span", { style: {
                                                            fontSize: '10px',
                                                            padding: '2px 6px',
                                                            borderRadius: '4px',
                                                            background: 'var(--waa-primary-50, rgba(59, 130, 246, 0.1))',
                                                            color: 'var(--waa-primary)',
                                                        }, children: [label, ": ", value] }, label))) })), _jsxs("p", { style: { fontSize: '11px', textAlign: 'center', margin: '4px 0 0', color: 'var(--waa-gray-500)' }, children: [t('quantity') || 'Qty', ": ", item.quantity] })] })] }, `${item.product_id}-${item.variation_id || 0}-${index}`));
                            }) }) })] }), _jsxs("div", { className: wac('mt-2 p-3 rounded-xl'), style: { background: 'var(--waa-gray-50)' }, children: [_jsxs("div", { className: wac('flex items-center justify-between mb-2'), children: [_jsxs("span", { className: wac('text-sm font-medium'), style: { color: 'var(--waa-gray-700)' }, children: [t('total'), ":"] }), _jsx("span", { className: wac('text-lg font-bold'), style: { color: 'var(--waa-primary)' }, children: total })] }), _jsxs("div", { className: wac('flex gap-2'), children: [cart_url && (_jsxs("button", { onClick: handleViewCart, className: wac('flex-1 flex items-center justify-center gap-2 text-sm font-semibold transition-opacity hover:opacity-80'), style: {
                                    padding: '8px 20px',
                                    border: '1.5px solid var(--waa-primary, #6366F1)',
                                    color: 'var(--waa-primary, #6366F1)',
                                    background: 'transparent',
                                    borderRadius: '999px',
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                }, children: [_jsx(ShoppingCart, { className: wac('w-4 h-4') }), t('viewCart')] })), checkout_url && (_jsxs("button", { onClick: handleCheckout, className: wac('flex-1 flex items-center justify-center gap-2 text-sm font-semibold transition-opacity hover:opacity-90'), style: {
                                    padding: '8px 20px',
                                    background: 'var(--waa-primary, #6366F1)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '999px',
                                    cursor: 'pointer',
                                    fontFamily: 'inherit',
                                }, children: [_jsx(CreditCard, { className: wac('w-4 h-4') }), t('checkout')] }))] })] })] }));
}
export default CartView;
